import { useQuery } from "@tanstack/react-query";
import { api, type Schedule, type ScheduleRun } from "../../lib/api";
import { Badge, Skeleton } from "../../components/ui/primitives";
import { formatWhen } from "./cadence";

/** Active, or paused with why. */
export function ScheduleStatus({ s }: { s: Pick<Schedule, "status" | "pausedReason"> }) {
  return s.status === "active" ? (
    <Badge tone="good">Active</Badge>
  ) : (
    <span className="inline-flex flex-col items-start gap-0.5">
      <Badge tone="warn">Paused</Badge>
      {s.pausedReason && <span className="text-xs text-subtle">{s.pausedReason}</span>}
    </span>
  );
}

const RUN_STATUS: Record<ScheduleRun["status"], { label: string; className: string }> = {
  sent: { label: "Sent", className: "bg-good/10 text-good" },
  built: { label: "Built", className: "" },
  failed: { label: "Failed", className: "bg-bad/10 text-bad" },
};

export function RunStatus({ run }: { run: ScheduleRun }) {
  const s = RUN_STATUS[run.status];
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Badge className={s.className}>{s.label}</Badge>
      {run.provisional && <Badge tone="info" title="A covered location's business day hadn't closed yet">Provisional</Badge>}
    </span>
  );
}

/** The latest run, for a list row. */
export function LastRun({ run }: { run: ScheduleRun | null }) {
  if (!run) return <span className="text-subtle">Not sent yet</span>;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <RunStatus run={run} />
      <span className="text-xs text-subtle" title={run.startedAt}>{formatWhen(run.startedAt)}</span>
    </span>
  );
}

const size = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`);

/** A schedule's last 20 runs, newest first (the owner's, or any for super users). */
export function RunHistory({ scheduleId }: { scheduleId: string }) {
  const detail = useQuery({ queryKey: ["schedule", scheduleId], queryFn: () => api.schedule(scheduleId) });
  if (detail.isLoading) return <div className="space-y-1.5">{[0, 1].map((i) => <Skeleton key={i} className="h-7" />)}</div>;
  if (detail.error) return <p className="text-sm text-bad">{detail.error.message}</p>;
  const d = detail.data!;
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-muted">
        <span className="font-medium text-foreground">Locations:</span> {d.locations.map((l) => l.name).join(", ")}
      </p>
      {!d.runs.length ? (
        <p className="text-xs text-subtle">No runs yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {d.runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-3 py-2">
              <span className="w-40 shrink-0 tabular text-muted" title={r.startedAt}>{formatWhen(r.startedAt)}</span>
              <RunStatus run={r} />
              <span className="min-w-0 flex-1 text-xs text-subtle" title={r.messageId ? `Message-ID ${r.messageId}` : undefined}>
                {r.status === "failed"
                  ? <span className="text-bad">{r.error}</span>
                  : [
                      r.recipients && `${r.recipients.length} recipient${r.recipients.length === 1 ? "" : "s"}`,
                      r.rowCount !== null && `${r.rowCount.toLocaleString()} rows`,
                      r.bytes !== null && size(r.bytes),
                      r.attempt && r.attempt > 1 && `attempt ${r.attempt}`,
                    ].filter(Boolean).join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
